/**
 * 상세의 첨부 자리 (2026-08-22 · 2026-08-23 재설계).
 *
 * 처음 판은 머리줄(개수·합계·모두 받기)을 얹고 목록은 채팅의 `AttachmentList` 를 그대로 썼다.
 * 쓰고 보니 두 가지가 걸렸다:
 *
 *   ① **열어 볼 길이 없다.** pdf·docx·xlsx·pptx·hwp 뷰어가 이 제품에 이미 있는데, 그 진입이
 *      `AttachmentList` 안에서 `roomId` 가 있을 때만(채팅 전용) 열린다 — 메일은 **받기만** 됐다.
 *   ② 이미지와 파일이 **한 줄에 섞여** 크기가 들쭉날쭉했다.
 *
 * 재설계 — 이미지는 **정사각 격자**로 먼저, 파일은 **한 줄 목록**으로. 파일에는 [보기]와 [받기].
 *
 * ★ [보기]는 **열 수 있는 형식에만** 붙는다(`attachmentPreviewDoc()` 이 판정). 눌러도 아무 일
 *   없는 버튼은 사용자에게 "고장" 이다 — 이 화면에서 이미 두 번 겪었다.
 * ★ '모두 받기' 는 서버에 묶음 내려받기 길이 없어 **차례로 각각** 받는다. 못 하는 것을 하는
 *   척하지 않고, 몇 개째인지와 실패 개수를 말해 준다.
 */

import { useCallback, useState } from 'react';
import { Download, Eye, Paperclip } from '../../icons';
import { requestPresignedDownload } from '../../api/files';
import { formatBytes, isImageMime } from '../../lib/attachments';
import { attachmentPreviewDoc, type PreviewDoc } from '../../lib/preview-doc';
import type { MessageAttachment } from '../../types/api';
import { tMail } from '../../lib/mail-i18n';
import { MailAttachThumb } from './MailAttachThumb';
import { MailDocViewer } from './MailDocViewer';
import { AttachmentSaveMenu } from '../../components/Attachment/AttachmentSaveMenu';
import { saveMailAttachmentToWorkspace } from '../../api/mail-attachment-save';
import './MailAttachments.css';

interface MailAttachmentsProps {
  attachments: MessageAttachment[];
  /**
   * 어느 메일의 첨부인가 (2026-09-02).
   *
   * 「워크스페이스에 저장」의 권한을 **메일 기준**으로 보기 때문에 필요하다(내가 보냈거나
   * 받은 메일의 첨부만 담을 수 있다). 안 넘기면 종전대로 **곧장 내려받기**만 한다 —
   * 이 컴포넌트를 다른 데서 쓰더라도 회귀가 없다.
   */
  mailId?: string;
}

/** 브라우저가 잇달아 받도록 조금씩 띄운다 — 한꺼번에 열면 대개 하나만 살아남는다. */
const GAP_MS = 350;

/** 한 개 받기 — 주소를 받아 앵커로 내려받는다. 실패는 부르는 쪽이 처리한다. */
async function downloadOne(a: MessageAttachment): Promise<void> {
  const res = await requestPresignedDownload(a.fileId);
  const el = document.createElement('a');
  el.href = res.downloadUrl;
  el.download = a.name ?? '';
  el.rel = 'noopener';
  document.body.appendChild(el);
  el.click();
  el.remove();
}

export function MailAttachments({ attachments, mailId }: MailAttachmentsProps) {
  const [progress, setProgress] = useState<{ i: number; n: number } | null>(null);
  const [failed, setFailed] = useState(0);
  const [viewerDoc, setViewerDoc] = useState<PreviewDoc | null>(null);

  /**
   * 「워크스페이스에 저장」 창구 — `mailId` 가 있을 때만 준다.
   * 없으면 `AttachmentSaveMenu` 가 갈림길 없이 곧장 내려받는다(종전 동작).
   */
  const saverFor = useCallback(
    (a: MessageAttachment) => (mailId
      ? (parentId: string | null) => saveMailAttachmentToWorkspace(mailId, a.fileId, { parentId, ...(a.name ? { name: a.name } : {}) })
      : undefined),
    [mailId],
  );

  const images = attachments.filter((a) => isImageMime(a.mimeType ?? ''));
  const files = attachments.filter((a) => !isImageMime(a.mimeType ?? ''));
  const total = attachments.reduce((sum, a) => sum + (typeof a.sizeBytes === 'number' ? a.sizeBytes : 0), 0);

  const downloadAll = useCallback(async () => {
    if (progress) return;
    setFailed(0);
    let bad = 0;
    for (let i = 0; i < attachments.length; i += 1) {
      setProgress({ i: i + 1, n: attachments.length });
      try {
        await downloadOne(attachments[i]!);
      } catch {
        // 한 개가 막혀도 나머지는 계속 받는다 — 열 개 중 하나 때문에 다 잃을 이유가 없다.
        bad += 1;
      }
      await new Promise((r) => setTimeout(r, GAP_MS));
    }
    setFailed(bad);
    setProgress(null);
  }, [attachments, progress]);

  if (attachments.length === 0) return null;

  return (
    <div className="mail-detail-attachments">
      <div className="mattp-head">
        <Paperclip size={13} aria-hidden />
        <b>{tMail('mail.attach.count').replace('{n}', String(attachments.length))}</b>
        {total > 0 && <span className="mattp-size">· {formatBytes(total)}</span>}
        <span className="mattp-gap" />
        {attachments.length > 1 && (
          <button type="button" className="mattp-all" onClick={() => void downloadAll()} disabled={progress !== null}>
            <Download size={13} aria-hidden />
            {progress
              ? tMail('mail.attach.saving_n').replace('{i}', String(progress.i)).replace('{n}', String(progress.n))
              : tMail('mail.attach.save_all')}
          </button>
        )}
      </div>

      {failed > 0 && (
        <p className="mattp-warn" role="status">
          {tMail('mail.attach.some_failed').replace('{n}', String(failed))}
        </p>
      )}

      {/* 사진은 격자로 — 정사각이라 몇 장이든 높이가 예측된다 */}
      {images.length > 0 && (
        <ul className="mattp-imgs" aria-label={tMail('mail.attach.images').replace('{n}', String(images.length))}>
          {images.map((a) => (
            <li key={a.fileId}>
              <AttachmentSaveMenu
                fileId={a.fileId}
                onDownload={() => downloadOne(a).catch(() => setFailed((n) => n + 1))}
                onSaveToWorkspace={saverFor(a)}
                className="mattp-img"
                buttonLabel={tMail('mail.attach.download_aria').replace('{name}', a.name ?? '')}
              >
                <MailAttachThumb fileId={a.fileId} name={a.name} mimeType={a.mimeType} size={104} />
                <span className="mattp-img-cap">{a.name}</span>
              </AttachmentSaveMenu>
            </li>
          ))}
        </ul>
      )}

      {/* 문서·그 밖의 파일은 한 줄 목록으로 */}
      {files.length > 0 && (
        <ul className="mattp-files">
          {files.map((a) => {
            const doc = attachmentPreviewDoc(a);
            return (
              <li key={a.fileId} className="mattp-file">
                <MailAttachThumb fileId={a.fileId} name={a.name} mimeType={a.mimeType} />
                <span className="mattp-file-main">
                  <span className="mattp-file-name" title={a.name ?? ''}>{a.name}</span>
                  <span className="mattp-file-size">{formatBytes(a.sizeBytes ?? 0)}</span>
                </span>
                <span className="mattp-file-tools">
                  {/* 열 수 있는 형식에만 — zip 에는 아예 안 그린다 */}
                  {doc && (
                    <button
                      type="button"
                      className="mattp-tool"
                      onClick={() => setViewerDoc(doc)}
                      aria-label={tMail('mail.attach.view_aria').replace('{name}', a.name ?? '')}
                      title={tMail('mail.attach.view')}
                    >
                      <Eye size={13} />
                    </button>
                  )}
                  <AttachmentSaveMenu
                    fileId={a.fileId}
                    onDownload={() => downloadOne(a).catch(() => setFailed((n) => n + 1))}
                    onSaveToWorkspace={saverFor(a)}
                    className="mattp-tool"
                    buttonLabel={tMail('mail.attach.download_aria').replace('{name}', a.name ?? '')}
                  >
                    <Download size={13} />
                  </AttachmentSaveMenu>
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {viewerDoc && <MailDocViewer doc={viewerDoc} onClose={() => setViewerDoc(null)} />}
    </div>
  );
}
